import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterRecordBanners from '@/matter-asset-common/asset/MatterRecordBanners'
import { MedChronAssetActions } from '../store/slice'
import { selectFailedFilesUpdate, selectIsUpdateActive, selectMutationError, selectMutationPending, selectUpdateStatusForAsset } from '../store/selectors'
import { MedChronUpdateStatus } from '../store/schemas'
import { activeStatusLabel } from '../utils/active-status-label'
import { useMedChronEditingLease } from '../hooks/useMedChronEditingLease'

interface MedChronAssetBannersProps {
  matterId: string
  assetId: string
  isTabHeaderMultiRow?: boolean
  /** Opens the version-history panel — wired to the failed-files banner's "Version info" action. */
  onShowVersionInfo?: () => void
}

/**
 * Status banners for the living chronology. Thin wrapper over the shared MatterRecordBanners that selects
 * the med-chron update state and supplies its progress copy and resolve action.
 */
export default function MedChronAssetBanners(props: MedChronAssetBannersProps) {
  const { matterId, assetId, isTabHeaderMultiRow = false, onShowVersionInfo } = props
  const dispatch = useAppDispatch()
  const lease = useMedChronEditingLease()

  const isReadOnly = !lease.canWrite
  const updateStatus = useAppSelector((state: RootState) => selectUpdateStatusForAsset(state, { assetId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const failedFilesUpdate = useAppSelector((state: RootState) => selectFailedFilesUpdate(state, { matterId }))
  const mutationPending = useAppSelector((state: RootState) => selectMutationPending(state, { assetId }))
  const mutationError = useAppSelector((state: RootState) => selectMutationError(state, { assetId }))

  const status = updateStatus?.update.status ?? MedChronUpdateStatus.PENDING
  const isErrored = updateStatus?.update.status === MedChronUpdateStatus.ERROR

  return (
    <MatterRecordBanners
      isReadOnly={isReadOnly}
      isUpdateActive={isUpdateActive}
      activeStatusLabel={activeStatusLabel(status, updateStatus?.progress)}
      failedFiles={
        failedFilesUpdate
          ? { updateId: failedFilesUpdate.update.id, displayNames: (failedFilesUpdate.failed_files ?? []).map((file) => file.display_name) }
          : null
      }
      erroredUpdateId={isErrored ? updateStatus.update.id : null}
      mutationPending={mutationPending}
      mutationError={typeof mutationError === 'string' ? mutationError : null}
      onCompleteExcludingFailed={(updateId) => dispatch(MedChronAssetActions.completeExcludingFailed({ assetId, updateId }))}
      onShowVersionInfo={onShowVersionInfo}
      isTabHeaderMultiRow={isTabHeaderMultiRow}
    />
  )
}
